<template>
	<div class="tabs">
		<div
			class="tabs__bar"
			role="tablist">
			<button
				v-for="item in props.items"
				:key="item.value"
				class="tabs__item"
				role="tab"
				type="button"
				:aria-selected="item.value === modelValue"
				:class="{ 'tabs__item--active': item.value === modelValue }"
				@click="select(item.value)">
				<span class="tabs__label">{{ item.label }}</span>
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
type TabItem = {
	label: string
	value: string
}

const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void
}>()

const props = defineProps<{
	items: TabItem[]
}>()

const modelValue = defineModel<string>({ default: '' })

function select(value: string) {
	emit('update:modelValue', value)
}
</script>

<style scoped lang="scss">
.tabs {
	position: sticky;
	top: 64px; /* below header toolbar */
	z-index: 900;
	background: var(--p-surface-0);
	border-bottom: 1px solid var(--p-surface-200);
	padding: 0; /* no side padding so center alignment is true-center */
	height: 44px; /* fixed bar height */

	@media all {
		background: var(--p-surface-950);
		border-bottom-color: var(--p-surface-900);
	}

	.tabs__bar {
		position: relative;
		display: flex;
		gap: 6px;
		flex-wrap: nowrap;
		overflow: auto;
		padding-bottom: 0;
		height: 44px; /* match container */
		align-items: flex-end; /* sit tabs on the baseline above the border */
		justify-content: center;
	}

	.tabs__item {
		appearance: none;
		border: 0;
		background: transparent;
		color: var(--p-text-color);
		font-weight: 600; /* keep consistent to prevent reflow */
		padding: 0 16px;
		border-radius: 10px 10px 0 0; /* tab shape */
		cursor: pointer;
		transition:
			color 0.15s ease,
			background 0.15s ease;
		outline: none;
		white-space: nowrap;
		height: 44px;
		display: inline-flex;
		align-items: center;

		@media all {
			background: transparent;
		}

		&:hover {
			color: var(--p-primary-500);
		}

		&--active {
			background: var(--p-surface-0);
			color: var(--p-primary-500);
			box-shadow: inset 0 -1px 0 0 #000000;

			@media all {
				box-shadow: inset 0 -1px 0 0 var(--p-yellow-500);
				background: var(--p-surface-950);
				color: var(--p-yellow-500);
			}
		}
	}
}
</style>
